import React, { useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { DEFAULT_GROUP } from './Droppable';
import classNames from 'classnames';
import fillStyle from './FillStyle.module.css';

export interface DraggableProps<DraggableItem> {
  /** The group this Draggable belongs to. Only Droppables with this group assigned to
   * it will accept this Draggable. If not set, a default group name is assigned instead.
   */
  group?: string | symbol;

  /** An object that will be emitted in the drag/drop events of whatever Droppable this Draggable
   * interacts with.
   *
   * Ideally, this object should describe the draggable as minimally as possible
   * (example: `{ id: yourItemId }`) to prevent from the Draggable and target Droppable
   * from getting overly coupled.
   */
  item: DraggableItem;

  /**
   * Prevents this item from being dragged
   */
  disabled?: boolean;

  /**
   * Whether or not to fill the parent
   */
  fill?: boolean;

  /**
   * Style to be applied to the drag wrapper
   */
  style?: React.CSSProperties;

  /**
   * Style to be applied to the drag wrapper while the item is being dragged. **NOTE:**
   * This only applies to the drag *source*, and not the item actually being dragged
   * around under the cursor. Useful for hiding or fading out an item while dragging it
   */
  dragStyle?: React.CSSProperties;

  /**
   * CSS class to be applied to the drag wrapper
   */
  className?: string;

  /**
   * CSS Class to be applied to the drag wrapper while the item is being dragged. **NOTE:**
   * This only applies to the drag *source*, and not the item actually being dragged
   * around under the cursor. Useful for hiding or fading out an item while dragging it
   */
  dragClassName?: string;

  /**
   * Called when dragging is initiated
   */
  onDragStart?(): void;

  /**
   * Called when a draggable is released regardless of where it was actually dropped
   */
  onDragEnd?(): void;

  /**
   * onDragStart and onDragEnd combined into a single event
   * @param isDragging Whether or not the item is being dragged
   */
  onDragChange?(isDragging: boolean): void;
}

const Draggable = <Item extends object>(
  props: DraggableProps<Item> & { children?: React.ReactNode }
) => {
  const {
    group,
    item,
    disabled,
    fill,
    style,
    dragStyle,
    className = '',
    dragClassName = '',
    onDragStart,
    onDragEnd,
    onDragChange,
    children,
  } = props;

  const [{ isDragging }, drag] = useDrag({
    type: group ?? DEFAULT_GROUP,
    item: () => item,
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disabled,
  });

  useEffect(() => {
    if (isDragging) {
      if (onDragStart) {
        onDragStart();
      }

      if (onDragChange) {
        onDragChange(true);
      }
    } else if (!isDragging) {
      if (onDragEnd) {
        onDragEnd();
      }

      if (onDragChange) {
        onDragChange(false);
      }
    }
  }, [isDragging]);

  return (
    <div
      ref={drag}
      style={isDragging ? { ...style, ...dragStyle } : style}
      className={classNames(className, {
        [dragClassName]: isDragging,
        [fillStyle.fill]: fill,
      })}
    >
      {children}
    </div>
  );
};

Draggable.displayName = 'Draggable';
export { Draggable };
